import type { Tables, TablesInsert, TablesUpdate } from "@shared/database.types.ts";
import useSWR, { type SWRConfiguration } from "swr";
import { supabaseClient } from "@/lib/supabase";
import { useAuth } from "../auth/auth-context";

export type StaffSupa = Tables<"staff">;
export type Subject = Tables<"subjects">;
export type StaffSupaInsert = TablesInsert<"staff">;
export type StaffSupaUpdate = TablesUpdate<"staff">;

// SWR configuration with deduplication and revalidation settings
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 600000, // 10 minute
  focusThrottleInterval: 30000, // 30 seconds
  errorRetryCount: 3,
  shouldRetryOnError: true,
};

import { STAFF_CACHE_KEYS } from "@/services/entities/cache-keys";

/**
 * Fetch a single tutor by ID
 * @param id Tutor ID
 * @returns Promise<Tutor>
 */
export async function fetchTutor(id: string) {
  const { data, error } = await supabaseClient
    .from("staff")
    .select("*, subjects(*)")
    .eq("staff_id", id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching tutor by id", error);
    throw error;
  }
  return data;
}

/**
 * Gets all tutors. Should be used by admin only since this returns all tutors, their emails, and other sensitive data.
 * @returns Promise<Tutor[]>
 */
export async function fetchAllTutors() {
  const { data, error } = await supabaseClient
    .from("staff")
    .select("*, subjects(*)")
    .eq("is_tutor", true);
  if (error) {
    console.error("Error fetching all tutors", error);
    throw error;
  }
  return data ?? [];
}

type StaffWithSubjects = StaffSupa & { subjects: Subject[] };

/**
 * Hook for accessing a single tutor's information, for the current user
 * @returns Tutor data and loading/error states
 */
export function useMyStaffData() {
  const { staffId } = useAuth();
  const {
    data = undefined,
    error,
    isLoading,
    mutate,
  } = useSWR<StaffWithSubjects | null | undefined>(
    staffId ? STAFF_CACHE_KEYS.MY_TUTOR_DATA(staffId) : null,
    () => fetchTutor(staffId),
    {
      ...swrConfig,
    },
  );

  const updateTutor = async (input: StaffSupaUpdate, subjects?: Subject[]) => {
    if (!data?.staff_id) {
      throw new Error("No current tutor id found to update");
    }
    if (subjects) {
      const insertData = subjects.map((subject) => ({
        staff_id: data.staff_id,
        subject_id: subject.subject_id,
      }));
      const { error: deleteError } = await supabaseClient
        .from("staff_subjects")
        .delete()
        .eq("staff_id", data.staff_id);
      if (deleteError) {
        console.error("Error deleting staff subjects", deleteError);
        throw deleteError;
      }
      const { error: insertError } = await supabaseClient.from("staff_subjects").insert(insertData);
      if (insertError) {
        console.error("Error inserting staff subjects", insertError);
        throw insertError;
      }
    }

    const { data: updatedStaff, error } = await supabaseClient
      .from("staff")
      .update(input)
      .eq("staff_id", data.staff_id)
      .select("*, subjects(*)")
      .single();

    if (error) {
      console.error("Error updating tutor", error);
      throw error;
    }

    mutate(updatedStaff, false);
    return updatedStaff;
  };

  return {
    staff: data,
    isLoading,
    error,
    updateTutor,
    refreshTutor: mutate,
  };
}

/**
 *
 * @param id The Id of a single tutor
 * @returns
 */
export function useStaff(id: string) {
  const { data, error, isLoading, mutate } = useSWR(
    STAFF_CACHE_KEYS.TUTOR(id),
    () => fetchTutor(id),
    swrConfig,
  );

  const updateStaff = async (vars: StaffSupaUpdate) => {
    const { data: updatedStaff, error } = await supabaseClient
      .from("staff")
      .update(vars)
      .eq("staff_id", id)
      .select("*, subjects(*)")
      .single();

    if (error) {
      console.error("Error updating tutor", error);
      throw error;
    }

    await mutate(updatedStaff, false);
    return updatedStaff;
  };

  return {
    staff: data,
    isLoading,
    error,
    refreshStaff: mutate,
    updateStaff: updateStaff,
  };
}

export function useTutors() {
  const { data, error, isLoading, mutate } = useSWR<StaffSupa[]>(
    STAFF_CACHE_KEYS.TUTORS,
    fetchAllTutors,
    swrConfig,
  );

  const createTutor = async (input: StaffSupaInsert) => {
    const { data: newTutor, error } = await supabaseClient
      .from("staff")
      .insert(input)
      .select("*, subjects(*)")
      .single();

    if (error) {
      console.error("Error creating tutor", error);
      throw error;
    }

    mutate((currentTutors) => [...(currentTutors ?? []), newTutor]);
    return newTutor;
  };

  const updateTutor = async (input: StaffSupaUpdate) => {
    if (!input.staff_id) {
      throw new Error("staff_id is required to update a tutor");
    }
    const { data: updatedTutor, error } = await supabaseClient
      .from("staff")
      .update(input)
      .eq("staff_id", input.staff_id)
      .select("*, subjects(*)")
      .single();

    if (error) {
      console.error("Error updating tutor", error);
      throw error;
    }

    mutate(
      (currentTutors) =>
        currentTutors?.map((t) => {
          if (t.staff_id === updatedTutor.staff_id) return updatedTutor;

          return t;
        }) ?? [],
    );
    return updatedTutor;
  };

  return {
    tutors: data || [],
    isLoading,
    error,
    createTutor,
    updateTutor,
    refreshTutors: mutate,
  };
}
